import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private sessionId: string | null = null;
  private sessionStartTime: Date | null = null;
  private lastScreen: string = '';
  private currentStoreId: string = '';
  private readonly logsApiUrl: string;
  private readonly SESSIONS_STORAGE_KEY = 'wibo_tracking_sessions';
  private readonly MAX_STORED_SESSIONS = 5;

  constructor(private http: HttpClient) {
    this.logsApiUrl = environment.logs_api_url;
  }

  get isActive(): boolean {
    return this.sessionId !== null;
  }

  get activeStoreId(): string {
    return this.currentStoreId;
  }

  startSession(storeId: string, userId?: string): string {
    this.sessionId = crypto.randomUUID();
    this.sessionStartTime = new Date();
    this.currentStoreId = storeId;
    this.lastScreen = '';
    this.track('session_started', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      user_id: userId || null,
    }, storeId);
    return this.sessionId;
  }

  endSession(reachedStep: string, storeId: string): void {
    const durationSeconds = this.sessionStartTime
      ? Math.floor((new Date().getTime() - this.sessionStartTime.getTime()) / 1000)
      : 0;
    this.track('session_ended', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      reached_step: reachedStep,
      duration_seconds: durationSeconds,
    }, storeId);
    this.sessionId = null;
    this.sessionStartTime = null;
    this.currentStoreId = '';
    this.lastScreen = '';
  }

  trackScreenNavigated(screenName: string, previousScreen: string, storeId: string): void {
    this.lastScreen = screenName;
    this.track('screen_navigated', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      screen_name: screenName,
      previous_screen: previousScreen,
    }, storeId);
  }

  trackProductTapped(productId: string, productName: string, categoryId: string, categoryName: string, storeId: string): void {
    this.track('product_tapped', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      product_id: productId,
      product_name: productName,
      category_id: categoryId,
      category_name: categoryName,
    }, storeId);
  }

  trackProductAddedToCart(productId: string, productName: string, categoryId: string, quantity: number, unitPrice: number, totalPrice: number, storeId: string): void {
    this.track('product_added_to_cart', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      product_id: productId,
      product_name: productName,
      category_id: categoryId,
      quantity,
      unit_price: unitPrice,
      total_price: totalPrice,
    }, storeId);
  }

  trackProductRemovedFromCart(productId: string, quantity: number, storeId: string): void {
    this.track('product_removed_from_cart', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      product_id: productId,
      quantity,
    }, storeId);
  }

  trackCheckoutStarted(cartTotal: number, itemsCount: number, storeId: string): void {
    this.track('checkout_started', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      cart_total: cartTotal,
      items_count: itemsCount,
    }, storeId);
  }

  trackCartAbandoned(lastStep: string, cartTotal: number, itemsCount: number, storeId: string): void {
    this.track('cart_abandoned', 'user_action', {
      channel: 'pwa',
      session_id: this.sessionId,
      last_step: lastStep,
      cart_total: cartTotal,
      items_count: itemsCount,
    }, storeId);
  }

  trackSatisfactionSubmitted(orderId: string, rating: number, storeId: string): void {
    const stored = this.getStoredSession(orderId);
    const originalSessionId = stored?.session_id || this.sessionId;
    const originalStoreId = stored?.store_id || storeId;
    this.track('satisfaction_submitted', 'user_action', {
      channel: 'pwa',
      session_id: originalSessionId,
      order_id: orderId,
      rating,
    }, originalStoreId, originalSessionId);
    this.removeOrderSession(orderId);
  }

  saveOrderSession(orderId: string, storeId: string): void {
    const sessions = this.getStoredSessions();
    const existing = sessions.findIndex(s => s.order_id === orderId);
    if (existing >= 0) {
      sessions[existing].session_id = this.sessionId!;
      sessions[existing].store_id = storeId;
    } else {
      sessions.push({ order_id: orderId, session_id: this.sessionId!, store_id: storeId, timestamp: Date.now() });
      if (sessions.length > this.MAX_STORED_SESSIONS) {
        sessions.shift();
      }
    }
    localStorage.setItem(this.SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
  }

  removeOrderSession(orderId: string): void {
    const sessions = this.getStoredSessions().filter(s => s.order_id !== orderId);
    localStorage.setItem(this.SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
  }

  private getStoredSession(orderId: string): { order_id: string; session_id: string; store_id: string; timestamp: number } | undefined {
    return this.getStoredSessions().find(s => s.order_id === orderId);
  }

  private getStoredSessions(): { order_id: string; session_id: string; store_id: string; timestamp: number }[] {
    try {
      return JSON.parse(localStorage.getItem(this.SESSIONS_STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  private getCurrentUserId(): string {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      return JSON.parse(raw)?.id || '';
    } catch {
      return '';
    }
  }

  private track(action: string, eventType: string, metadata: Record<string, any>, storeId: string, overrideSessionId?: string | null): void {
    const sessionId = overrideSessionId !== undefined ? overrideSessionId : this.sessionId;
    
    const payload = {
      event_name: action,
      event_type: eventType.toLowerCase(),
      action,
      severity: 'low',
      user_id: this.getCurrentUserId(),
      session_id: sessionId,
      business_context: { store_id: storeId },
      metadata: { ...metadata, channel: 'pwa', session_id: sessionId },
    };

    this.http.post(`${this.logsApiUrl}/v1/tracking/track`, payload)
      .pipe(
        timeout(5000),
        retry(1),
        catchError(() => EMPTY)
      )
      .subscribe();
  }
}
